import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PermissionGuard, usePermissions, type PermissionCheck } from '@/features/auth';
import { Alert, Badge, Mapper, Tabs, ButtonLink, TextLink } from '@/shared/components';
import { DealerBankAccounts } from '../components/DealerBankAccounts';
import { DealerConfigSettings } from '../components/DealerConfigSettings';
import { DealerContacts } from '../components/DealerContacts';
import { DealerFinancialsTab } from '../components/DealerFinancials/DealerFinancialsTab';
import { DealerGroupMembers } from '../components/DealerGroupMembers';
import { DealerProducts } from '../components/DealerProducts';
import { DealerSecurityGroups } from '../components/DealerSecurityGroups';
import { DealerStatusPanel } from '../components/DealerStatusPanel';
import { DealerUsers } from '../components/DealerUsers';
import { FranchisesTab } from '../components/FranchisesTab';
import { OemsTab } from '../components/OemsTab';
import { useDealerQuery } from '../hooks/useDealerQuery';
import { useDealersByIds } from '../hooks/useDealersByIds';
import { canBeFranchised, canFranchise, canOwnChildren } from '../utils/dealerParentageRules';
import { DEALER_TYPE_LABELS, type Dealer, type DealerType } from '../types/dealers.types';
import { statusVariant } from '../utils/statusVariant';

type Tab = 'details' | 'bank-accounts' | 'contacts' | 'group' | 'oems' | 'franchises' | 'security-groups' | 'users' | 'products' | 'financials' | 'config';

const ALL_TABS: {
    id: Tab;
    label: string;
    groupOnly?: boolean;
    show?: (type: DealerType) => boolean;
    /** Permission-gated tabs (UI convenience only — the backend permission
     *  middleware on the underlying routes is the security boundary). */
    permission?: PermissionCheck;
}[] = [
    { id: 'details', label: 'Details' },
    { id: 'bank-accounts', label: 'Bank Accounts' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'group', label: 'Owned Dealers', groupOnly: true },
    { id: 'oems', label: 'OEMs', show: canBeFranchised },
    { id: 'franchises', label: 'Franchises', show: canFranchise },
    { id: 'security-groups', label: 'Security Groups' },
    { id: 'users', label: 'Users' },
    { id: 'products', label: 'Products' },
    { id: 'financials', label: 'Financials', permission: { resource: 'ledger', action: 'dealer-statement.read' } },
    { id: 'config', label: 'Config', permission: { resource: 'dealer', action: 'config-setting.browse' } },
];

const DETAIL_ROW_CLASSES = 'border-b border-mp-border';
const DETAIL_HEADER_CLASSES = 'px-3 py-2 text-left';
const DETAIL_CELL_CLASSES = 'px-3 py-2 text-left';

const initialTab = (params: URLSearchParams): Tab => (params.get('tab') === 'financials' ? 'financials' : 'details');

function DealerDetailsPanel({ dealer }: { dealer: Dealer; }) {
    // Bridge: fall back to 'dealer' only for redux-persist payloads cached
    // before the `type` column shipped; removable once the persisted cache
    // has rotated (follow-up). Ownership no longer derives from `is_group`.
    const effectiveType = dealer.type ?? 'dealer';
    const parentId = dealer.parent_dealer_id ?? undefined;
    const { data: parentDealers, isLoading: parentLoading } = useDealersByIds(parentId ? [parentId] : []);
    const parent = parentDealers?.[0];
    const hasLocation =
        dealer.latitude != null && dealer.longitude != null &&
        !isNaN(Number(dealer.latitude)) && !isNaN(Number(dealer.longitude));

    const position: [number, number] = hasLocation
        ? [Number(dealer.latitude), Number(dealer.longitude)]
        : [0, 0];

    return (
        <div className="space-y-6 max-w-2xl">
            <table className="w-full text-sm">
                <tbody>
                    <tr className={DETAIL_ROW_CLASSES}>
                        <th className="px-3 py-2 text-left w-40">Dealer Code</th>
                        <td className="px-3 py-2 text-left font-mono text-xs">{dealer.dealer_code}</td>
                    </tr>
                    <tr className={DETAIL_ROW_CLASSES}>
                        <th className={DETAIL_HEADER_CLASSES}>Name</th>
                        <td className="px-3 py-2 text-left font-semibold">{dealer.name}</td>
                    </tr>
                    {dealer.trading_name && (
                        <tr className={DETAIL_ROW_CLASSES}>
                            <th className={DETAIL_HEADER_CLASSES}>Trading Name</th>
                            <td className={DETAIL_CELL_CLASSES}>{dealer.trading_name}</td>
                        </tr>
                    )}
                    <tr className={DETAIL_ROW_CLASSES}>
                        <th className={DETAIL_HEADER_CLASSES}>Status</th>
                        <td className={DETAIL_CELL_CLASSES}>
                            <Badge size="xs" variant={statusVariant(dealer.status_key)}>
                                {dealer.status?.label ?? dealer.status_key}
                            </Badge>
                        </td>
                    </tr>
                    <tr className={DETAIL_ROW_CLASSES}>
                        <th className={DETAIL_HEADER_CLASSES}>Type</th>
                        <td className={DETAIL_CELL_CLASSES}>
                            <Badge size="xs" variant="info">
                                {DEALER_TYPE_LABELS[effectiveType]}
                            </Badge>
                        </td>
                    </tr>
                    {parentId && (
                        <tr className={DETAIL_ROW_CLASSES}>
                            <th className={DETAIL_HEADER_CLASSES}>Parent</th>
                            <td className={DETAIL_CELL_CLASSES}>
                                {parentLoading && <span className="text-mp-muted text-sm">Loading...</span>}
                                {!parentLoading && parent && (
                                    <TextLink to={`/admin/dealers/${parent.id}`} color="secondary" className="text-sm">{parent.name}</TextLink>
                                )}
                                {!parentLoading && !parent && <span className="text-mp-muted text-sm">—</span>}
                            </td>
                        </tr>
                    )}
                    {dealer.email && (
                        <tr className={DETAIL_ROW_CLASSES}>
                            <th className={DETAIL_HEADER_CLASSES}>Email</th>
                            <td className={DETAIL_CELL_CLASSES}>{dealer.email}</td>
                        </tr>
                    )}
                    {dealer.phone && (
                        <tr className={DETAIL_ROW_CLASSES}>
                            <th className={DETAIL_HEADER_CLASSES}>Phone</th>
                            <td className={DETAIL_CELL_CLASSES}>{dealer.phone}</td>
                        </tr>
                    )}
                    {dealer.website && (
                        <tr className={DETAIL_ROW_CLASSES}>
                            <th className={DETAIL_HEADER_CLASSES}>Website</th>
                            <td className={DETAIL_CELL_CLASSES}>
                                <TextLink href={dealer.website} target="_blank" rel="noreferrer" color="secondary">
                                    {dealer.website}
                                </TextLink>
                            </td>
                        </tr>
                    )}
                    {dealer.franchise_code && (
                        <tr className={DETAIL_ROW_CLASSES}>
                            <th className={DETAIL_HEADER_CLASSES}>Franchise Code</th>
                            <td className="px-3 py-2 text-left font-mono text-xs">{dealer.franchise_code}</td>
                        </tr>
                    )}
                    <tr className={DETAIL_ROW_CLASSES}>
                        <th className={DETAIL_HEADER_CLASSES}>Address</th>
                        <td className={DETAIL_CELL_CLASSES}>
                            <div>{dealer.address_line_1}</div>
                            {dealer.address_line_2 && <div>{dealer.address_line_2}</div>}
                            <div>{dealer.city}</div>
                            <div>{dealer.postcode}</div>
                        </td>
                    </tr>
                    {hasLocation && (
                        <tr className={DETAIL_ROW_CLASSES}>
                            <th className={DETAIL_HEADER_CLASSES}>Coordinates</th>
                            <td className="px-3 py-2 text-left font-mono text-xs">
                                {dealer.latitude}, {dealer.longitude}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {hasLocation && (
                <Mapper
                    markers={[{
                        position,
                        popupTitle: dealer.name,
                        popupContent: dealer.address_line_1,
                    }]}
                    zoom={16}
                    center={position}
                />
            )}

            <div className="border-t border-mp-border pt-6">
                <DealerStatusPanel
                    dealerId={dealer.id}
                    status={dealer.status_key}
                    childDealersCount={dealer.child_dealers_count ?? 0}
                />
            </div>
        </div>
    );
}

export function DealerDetail() {
    const { id = '' } = useParams();
    const [params] = useSearchParams();
    const [activeTab, setActiveTab] = useState<Tab>(() => initialTab(params));
    const [tabFor, setTabFor] = useState(id);
    const { hasPermission } = usePermissions();

    if (tabFor !== id) {
        setTabFor(id);
        setActiveTab(initialTab(params));
    }

    const query = useDealerQuery(id);

    if (!id) {
        return <p className="text-mp-error">Invalid dealer ID.</p>;
    }

    if (query.isLoading) {
        return <p>Loading dealer...</p>;
    }

    if (query.isError || !query.data) {
        return (
            <Alert variant="error">
                <span>Dealer not found or failed to load.</span>
            </Alert>
        );
    }

    const dealer = query.data;
    const effectiveType = dealer.type ?? 'dealer';
    const canOwn = canOwnChildren(effectiveType);
    const tabs = ALL_TABS.filter((tab) =>
        (!tab.groupOnly || canOwn)
        && (!tab.show || tab.show(effectiveType))
        // UI convenience only: the config-settings and fund-statement routes
        // are the security boundary (their permission middleware).
        && (!tab.permission || hasPermission(tab.permission)));

    // Edge case: if the active tab is 'group'/'oems'/'franchises' but the type
    // loses that capability (e.g. type changed elsewhere and this page
    // refetches), fall back to 'details' rather than rendering a hidden tab's
    // content.
    const stillVisible = tabs.some((tab) => tab.id === activeTab);
    const effectiveActiveTab: Tab = stillVisible ? activeTab : 'details';

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to="/admin/dealers" color="secondary" className="text-sm">
                    &larr; Back to Dealers
                </TextLink>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold">{dealer.name}</h1>
                    {dealer.status_key && (
                        <Badge size="xs" variant={statusVariant(dealer.status_key)}>
                            {dealer.status?.label ?? dealer.status_key}
                        </Badge>
                    )}
                    {canOwn && (dealer.child_dealers_count ?? 0) > 0 && (
                        <button
                            type="button"
                            onClick={() => setActiveTab('group')}
                            className="cursor-pointer rounded-md"
                            aria-label={`${dealer.child_dealers_count} owned dealers — open Owned Dealers tab`}
                        >
                            <Badge variant="secondary">Owned: {dealer.child_dealers_count}</Badge>
                        </button>
                    )}
                </div>
                <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                    <ButtonLink to={`/admin/dealers/${id}/edit`} variant="outline" size="xs">
                        Edit Dealer
                    </ButtonLink>
                </PermissionGuard>
            </div>

            <Tabs
                tabs={tabs}
                activeTab={effectiveActiveTab}
                onTabChange={setActiveTab}
                idPrefix="dealer-detail"
                ariaLabel="Dealer sections"
            >
                {effectiveActiveTab === 'details' && <DealerDetailsPanel dealer={dealer} />}
                {effectiveActiveTab === 'bank-accounts' && <DealerBankAccounts dealerId={id} />}
                {effectiveActiveTab === 'contacts' && <DealerContacts dealerId={id} />}
                {effectiveActiveTab === 'group' && canOwn && <DealerGroupMembers dealerId={id} />}
                {effectiveActiveTab === 'oems' && <OemsTab dealerId={id} dealerName={dealer.name} />}
                {effectiveActiveTab === 'franchises' && <FranchisesTab dealerId={id} dealerName={dealer.name} />}
                {effectiveActiveTab === 'security-groups' && <DealerSecurityGroups dealerId={id} dealerName={dealer.name} />}
                {effectiveActiveTab === 'users' && <DealerUsers dealerId={id} />}
                {effectiveActiveTab === 'products' && <DealerProducts dealerId={id} />}
                {effectiveActiveTab === 'financials' && <DealerFinancialsTab key={id} dealerId={id} includesOwnedDealers={canOwn} />}
                {effectiveActiveTab === 'config' && <DealerConfigSettings dealerId={id} />}
            </Tabs>
        </section>
    );
}
